import { AppShellWrapper } from '@/components/app-shell-wrapper'
import { PageTracker } from '@/routing/page-tracker'
import { ErrorPage } from '@shared/components/error-page/error-page'
import { useMemo } from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import { createBrowserRouter, RouterProvider } from 'react-router-dom'
import { routes } from '@/routing/routes'
import { ProtectedRoute } from '@/routing/protected-route'
import { RoleProtectedRoute } from '@/routing/role-protected-route'
// Routed pages
import { AdminPage } from '@/pages/admin/admin.page'
import { HomePage } from '@/pages/home.page'
import { LoggedOutPage } from '@/pages/logged-out.page'
import { CreateOfferPage } from '@/pages/offers/create-offer/create-offer.page'
import OffersLayout from '@/pages/offers/offers-layout'
import { OffersPage } from '@/pages/offers/offers.page'
import { PageNotFound } from '@/pages/page-not-found.page'
import { StationSearchPage } from '@/pages/stations/search/station-search.page'
import { EditSpeciPage } from '@/pages/stations/speci/edit-speci/edit-speci.page'
import { ViewSpeciPage } from '@/pages/stations/speci/view-speci/view-speci.page'
import { StationLayout } from '@/pages/stations/station-layout'
import { StationPage } from '@/pages/stations/station.page'
import { TypingProgram } from '@/pages/typing/program/typing-program.page'
import { TypedLotsPage } from '@/pages/typing/typed-lots/typed-lots.page'
import { WoolStoreToolsPage } from '@/pages/wool-store/wool-store-tools.page'
import { ViewOfferPage } from '@/pages/offers/view-offers/view-offer.page'
import { SpeciAction } from '@shared/components/speci/speci-editor/speci-editor'

export const routerConfig = [
  {
    element: (
      <ErrorBoundary fallbackRender={ErrorPage}>
        <PageTracker />
        <ProtectedRoute />
      </ErrorBoundary>
    ),
    children: [
      {
        path: '/',
        element: <AppShellWrapper />,
        children: [
          {
            path: routes.home.path,
            element: <HomePage />, // Home is always visible if you have a role
          },
          {
            path: routes.stationSearch.path,
            element: (
              <RoleProtectedRoute allowedRoles={['AllAccessUser']}>
                <StationSearchPage />
              </RoleProtectedRoute>
            ),
          },
          {
            path: routes.stationsModule.path,
            element: (
              <RoleProtectedRoute allowedRoles={['AllAccessUser']}>
                <StationLayout />
              </RoleProtectedRoute>
            ),
            children: [
              {
                path: routes.newSpeci.path,
                element: (
                  <RoleProtectedRoute allowedRoles={['AllAccessUser']}>
                    <EditSpeciPage action={SpeciAction.NEW} />
                  </RoleProtectedRoute>
                ),
              },
              {
                path: routes.editSpeci.path,
                element: <EditSpeciPage />,
              },
              {
                path: routes.stationsModule.path,
                element: <StationPage />,
              },
              {
                path: routes.stationPageTab.path,
                element: <StationPage />,
              },
              {
                path: routes.woolDataTab.path,
                element: <StationPage />,
              },
            ],
          },
          {
            path: routes.viewSpeci.path,
            element: (
              <RoleProtectedRoute allowedRoles={['AllAccessUser', 'WoolStoreUser']}>
                <ViewSpeciPage />
              </RoleProtectedRoute>
            ),
          },
          {
            path: routes.offers.path,
            element: (
              <RoleProtectedRoute allowedRoles={['AllAccessUser']}>
                <OffersLayout />
              </RoleProtectedRoute>
            ),
            children: [
              {
                path: routes.offers.path,
                element: <OffersPage />,
              },
              {
                path: routes.offersTab.path,
                element: <OffersPage />,
              },
              {
                path: routes.newOffer.path,
                element: <CreateOfferPage />,
              },
              {
                path: routes.viewOffer.path,
                element: <ViewOfferPage />,
              },
            ],
          },
          {
            path: routes.typingProgram.path,
            element: (
              <RoleProtectedRoute allowedRoles={['AllAccessUser']}>
                <TypingProgram />
              </RoleProtectedRoute>
            ),
          },
          {
            path: routes.typedLots.path,
            element: (
              <RoleProtectedRoute allowedRoles={['AllAccessUser']}>
                <TypedLotsPage />
              </RoleProtectedRoute>
            ),
          },
          {
            path: routes.woolStoreTools.path,
            element: (
              <RoleProtectedRoute allowedRoles={['WoolStoreUser', 'AllAccessUser']}>
                <WoolStoreToolsPage />
              </RoleProtectedRoute>
            ),
          },
          {
            path: routes.admin.path,
            element: (
              <RoleProtectedRoute allowedRoles={['AllAccessUser']}>
                <AdminPage />
              </RoleProtectedRoute>
            ),
          },
        ],
      },
    ],
  },
  {
    children: [
      {
        path: '/logged-out',
        element: <LoggedOutPage />,
      },
      {
        path: '*',
        element: <PageNotFound />,
      },
    ],
  },
]

export function Router() {
  const router = useMemo(
    () =>
      createBrowserRouter(routerConfig, {
        future: {
          v7_relativeSplatPath: true,
          v7_fetcherPersist: true,
          v7_normalizeFormMethod: true,
          v7_partialHydration: true,
          v7_skipActionErrorRevalidation: true,
        },
      }),
    []
  )

  const routerProvider = useMemo(
    () => (
      <RouterProvider
        router={router}
        future={{
          v7_startTransition: true,
        }}
      />
    ),
    [router]
  )

  return routerProvider
}
